'use client'

import {
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState
} from 'react'

import { MapPin, Search } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'

import { env } from '@/lib'
import { geocodingService } from '@/lib/geocoding/geocoding-service'
import { ReactMapComponent } from '@/lib/map'

import { MapPosition } from '@/lib/map/types'

interface MapModalProps {
  initialPosition?: MapPosition
  isOpen: boolean
  onClose: () => void
  onConfirm: (position: MapPosition, address?: string) => void
  title: string
}

export function MapModal({
  initialPosition,
  isOpen,
  onClose,
  onConfirm,
  title
}: MapModalProps) {
  const t = useTranslations('components.form.mapSelector.mapModal')
  const [selectedPosition, setSelectedPosition] = useState<MapPosition | null>(
    initialPosition || null
  )
  const [selectedAddress, setSelectedAddress] = useState<string>('')
  const [isLoadingAddress, setIsLoadingAddress] = useState(false)
  const [searchValue, setSearchValue] = useState<string>('')
  const [isSelectingPlace, setIsSelectingPlace] = useState(false)
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null)
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([])
  const [isPredictionsOpen, setIsPredictionsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const autocompleteServiceRef =
    useRef<google.maps.places.AutocompleteService | null>(null)
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null)
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null)
  const debounceRef = useRef<null | ReturnType<typeof setTimeout>>(null)

  /** Loads marker + search field from coordinates (no autocomplete-focus guard). */
  const geocodePosition = useCallback(
    async (position: MapPosition) => {
      try {
        const geocodingResult = await geocodingService.reverseGeocode(position)
        if (geocodingResult) {
          const longAddress = geocodingService.getShortAddress(geocodingResult)
          setSelectedAddress(longAddress)
          setSearchValue(longAddress)
        } else {
          setSelectedAddress(t('unknownLocation'))
          setSearchValue('')
        }
      } catch (error) {
        console.error('Error getting address:', error)
        setSelectedAddress(t('unknownLocation'))
        setSearchValue('')
      } finally {
        setIsLoadingAddress(false)
      }
    },
    [t]
  )

  const loadAddressForPosition = useCallback(
    async (position: MapPosition) => {
      setSelectedPosition(position)
      setIsLoadingAddress(true)
      await geocodePosition(position)
    },
    [geocodePosition]
  )

  const handleMapClick = useCallback(
    async (position: MapPosition) => {
      await loadAddressForPosition(position)
    },
    [loadAddressForPosition]
  )

  const ensurePlacesServices = useCallback((map: google.maps.Map) => {
    if (typeof window === 'undefined' || !window.google?.maps?.places) {
      return
    }
    if (!autocompleteServiceRef.current) {
      autocompleteServiceRef.current =
        new window.google.maps.places.AutocompleteService()
    }
    if (!placesServiceRef.current) {
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        map
      )
    }
    if (!sessionTokenRef.current) {
      sessionTokenRef.current =
        new window.google.maps.places.AutocompleteSessionToken()
    }
  }, [])

  const fetchPredictions = useCallback((input: string) => {
    const service = autocompleteServiceRef.current
    if (!service || !input.trim()) {
      setPredictions([])
      setIsPredictionsOpen(false)
      return
    }
    service.getPlacePredictions(
      {
        componentRestrictions: { country: 'eg' },
        input,
        sessionToken: sessionTokenRef.current ?? undefined
      },
      (results) => {
        const list = results ?? []
        setPredictions(list)
        setActiveIndex(0)
        setIsPredictionsOpen(list.length > 0)
      }
    )
  }, [])

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value)
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (!value.trim()) {
        setPredictions([])
        setIsPredictionsOpen(false)
        return
      }
      debounceRef.current = setTimeout(() => fetchPredictions(value), 200)
    },
    [fetchPredictions]
  )

  const handleSelectPrediction = useCallback(
    (prediction: google.maps.places.AutocompletePrediction) => {
      const service = placesServiceRef.current
      if (!service) return

      setIsSelectingPlace(true)
      setIsPredictionsOpen(false)
      setSearchValue(prediction.description)

      service.getDetails(
        {
          fields: ['formatted_address', 'geometry', 'name', 'place_id'],
          placeId: prediction.place_id,
          sessionToken: sessionTokenRef.current ?? undefined
        },
        async (place, status) => {
          sessionTokenRef.current =
            new window.google.maps.places.AutocompleteSessionToken()

          if (
            status !== window.google.maps.places.PlacesServiceStatus.OK ||
            !place?.geometry?.location
          ) {
            setIsSelectingPlace(false)
            return
          }

          const position: MapPosition = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          }

          if (mapInstance) {
            mapInstance.setCenter(position)
            mapInstance.setZoom(15)
          }

          setSelectedPosition(position)
          setIsLoadingAddress(true)

          const formattedAddress =
            place.formatted_address || place.name || prediction.description
          setSearchValue(formattedAddress)

          try {
            const geocodingResult =
              await geocodingService.reverseGeocode(position)
            if (geocodingResult) {
              setSelectedAddress(
                geocodingService.getShortAddress(geocodingResult)
              )
            } else {
              setSelectedAddress(formattedAddress || t('unknownLocation'))
            }
          } catch (error) {
            console.error('Error getting address:', error)
            setSelectedAddress(formattedAddress || t('unknownLocation'))
          } finally {
            setIsLoadingAddress(false)
            setTimeout(() => setIsSelectingPlace(false), 200)
          }
        }
      )
    },
    [mapInstance, t]
  )

  const handleMapReady = useCallback(
    (map: google.maps.Map) => {
      setMapInstance(map)
      ensurePlacesServices(map)
    },
    [ensurePlacesServices]
  )

  const handleConfirm = () => {
    if (selectedPosition) {
      onConfirm(selectedPosition, selectedAddress || undefined)
      onClose()
    }
  }

  const handleClose = () => {
    if (isSelectingPlace) return
    if (isPredictionsOpen) {
      setIsPredictionsOpen(false)
      return
    }
    setSelectedAddress('')
    setIsLoadingAddress(false)
    onClose()
  }

  // Sync from props only when the dialog opens — not when handleMapClick or
  // autocomplete focus changes, or the modal would reset after a new search selection.
  const [prevIsOpen, setPrevIsOpen] = useState(false)
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen)
    if (!isOpen) {
      setSearchValue('')
      setPredictions([])
      setIsPredictionsOpen(false)
    } else if (initialPosition) {
      setSelectedPosition(initialPosition)
      setIsLoadingAddress(true)
    } else {
      setSelectedPosition(null)
      setSelectedAddress('')
      setSearchValue('')
    }
  }

  const geocodeInitialPosition = useEffectEvent(() => {
    if (initialPosition) void geocodePosition(initialPosition)
  })

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- state is only set after awaiting the external geocoder
    if (isOpen) geocodeInitialPosition()
  }, [isOpen])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open) {
          handleClose()
        }
      }}
      open={isOpen}
    >
      <DialogContent
        className="h-[70vh] max-h-[800px] w-[95vw] max-w-4xl overflow-hidden !rounded-xl p-0 md:h-[60vh] md:w-[50vw]"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <div className="flex h-full flex-col">
          <DialogHeader className="px-6 pt-6 pb-4">
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              {title}
            </DialogTitle>
          </DialogHeader>

          <div className="relative z-50 flex flex-col gap-3 px-6 pb-4">
            <Popover
              onOpenChange={(open) => {
                if (!open) setIsPredictionsOpen(false)
              }}
              open={isPredictionsOpen && predictions.length > 0}
            >
              <PopoverAnchor asChild>
                <div className="relative">
                  <Search className="text-muted-foreground absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2" />
                  <Input
                    className="ps-10"
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onFocus={() => {
                      if (predictions.length > 0) setIsPredictionsOpen(true)
                    }}
                    onKeyDown={(e) => {
                      if (!isPredictionsOpen || predictions.length === 0) return
                      if (e.key === 'ArrowDown') {
                        e.preventDefault()
                        setActiveIndex((i) =>
                          Math.min(i + 1, predictions.length - 1)
                        )
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault()
                        setActiveIndex((i) => Math.max(i - 1, 0))
                      } else if (e.key === 'Enter') {
                        e.preventDefault()
                        const pick = predictions[activeIndex]
                        if (pick) handleSelectPrediction(pick)
                      } else if (e.key === 'Escape') {
                        setIsPredictionsOpen(false)
                      }
                    }}
                    placeholder="Search for a place..."
                    ref={inputRef}
                    type="text"
                    value={searchValue}
                  />
                </div>
              </PopoverAnchor>
              <PopoverContent
                align="start"
                className="w-[var(--radix-popover-trigger-width)] p-1"
                onInteractOutside={(e) => {
                  if (inputRef.current?.contains(e.target as Node)) {
                    e.preventDefault()
                  }
                }}
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <ul className="max-h-64 overflow-auto">
                  {predictions.map((p, idx) => (
                    <li key={p.place_id}>
                      <button
                        className={`hover:bg-accent flex w-full items-start gap-2 rounded-sm px-2 py-2 text-start text-sm ${
                          idx === activeIndex ? 'bg-accent' : ''
                        }`}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          handleSelectPrediction(p)
                        }}
                        onMouseEnter={() => setActiveIndex(idx)}
                        type="button"
                      >
                        <MapPin className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {p.structured_formatting?.main_text ??
                              p.description}
                          </span>
                          {p.structured_formatting?.secondary_text && (
                            <span className="text-muted-foreground text-xs">
                              {p.structured_formatting.secondary_text}
                            </span>
                          )}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </PopoverContent>
            </Popover>
          </div>

          <div className="relative flex-1 sm:px-4">
            <ReactMapComponent
              apiKey={env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}
              center={selectedPosition || { lat: 30.0444, lng: 31.2357 }} // Cairo coordinates as default
              className="h-full w-full rounded-sm"
              hideControl
              markers={
                selectedPosition
                  ? [
                      {
                        description: `${Number(selectedPosition.lat).toFixed(6)}, ${Number(selectedPosition.lng).toFixed(6)}`,
                        id: 'selected-location',
                        position: selectedPosition,
                        title: t('selectedLocation')
                      }
                    ]
                  : []
              }
              onMapClick={handleMapClick}
              onMapReady={handleMapReady}
              zoom={13}
            />

            {selectedPosition && (
              <div className="bg-card absolute start-10 top-4 max-w-[calc(100%-5rem)] rounded-lg border p-3 shadow-lg md:max-w-sm">
                <div className="text-sm font-medium">
                  {t('selectedLocation')}
                </div>
                {isLoadingAddress ? (
                  <div className="text-muted-foreground text-xs">
                    {t('loadingAddress')}
                  </div>
                ) : selectedAddress ? (
                  <div className="text-muted-foreground text-xs">
                    {selectedAddress}
                  </div>
                ) : null}
                <div className="text-muted-foreground text-xs">
                  {Number(selectedPosition.lat).toFixed(6)},{' '}
                  {Number(selectedPosition.lng).toFixed(6)}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex w-full flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-muted-foreground text-sm">
              {t('clickToSelect')}
            </div>
            <div className="flex gap-3">
              <Button onClick={handleClose} variant="outline">
                {t('cancel')}
              </Button>
              <Button disabled={!selectedPosition} onClick={handleConfirm}>
                {t('confirmLocation')}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
